"use client";

import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "@/components/common/Link";
import { Search, MapPin, Layers, BadgeCheck, Star, X } from "lucide-react";

/**
 * Services Hub page — Section 7: Service Areas
 *
 * Mirrors EmergencyServiceAreas 1:1 (dark navy bg + search + chip filter +
 * 10-region grid + bottom CTA banner).
 *
 * Differentiation:
 *   - Eyebrow: "WHERE WE WORK" (vs "EMERGENCY COVERAGE")
 *   - H2: broad coverage framing, not emergency-speed
 *   - Stat #3 swapped: "7 Days a Week" (vs "Same Day Emergency Response")
 *
 * Total: 217+ suburbs across 10 regions = Sydney (8) + Wollongong + Central Coast
 */

const HEAD = {
  fontFamily: "'Manrope', sans-serif"
};
const LABEL = {
  fontFamily: "'Manrope', sans-serif"
};
type Region = {
  name: string;
  suburbs: string[];
};
const regions: Region[] = [{
  name: "Eastern Suburbs",
  suburbs: ["Bellevue Hill", "Bondi", "Bondi Beach", "Bondi Junction", "Botany", "Bronte", "Centennial Park", "Clovelly", "Coogee", "Double Bay", "Dover Heights", "Eastgardens", "Kensington", "Kingsford", "Maroubra", "Mascot", "Paddington", "Point Piper", "Randwick", "Rose Bay", "Vaucluse", "Watsons Bay", "Waverley", "Woollahra", "Zetland"]
}, {
  name: "Inner West",
  suburbs: ["Annandale", "Ashfield", "Balmain", "Burwood", "Camperdown", "Concord", "Croydon", "Dulwich Hill", "Enmore", "Five Dock", "Glebe", "Haberfield", "Leichhardt", "Marrickville", "Newtown", "Petersham", "Rozelle", "Stanmore", "Strathfield", "Summer Hill", "Surry Hills", "Tempe"]
}, {
  name: "North Shore",
  suburbs: ["Artarmon", "Castle Cove", "Chatswood", "Cremorne", "Crows Nest", "Frenchs Forest", "Gordon", "Hornsby", "Killara", "Kirribilli", "Lane Cove", "Lindfield", "Longueville", "Mosman", "Neutral Bay", "North Sydney", "Pymble", "Roseville", "Ryde", "St Ives", "Turramurra", "Wahroonga", "Willoughby"]
}, {
  name: "Northern Beaches",
  suburbs: ["Avalon", "Balgowlah", "Brookvale", "Collaroy", "Curl Curl", "Dee Why", "Freshwater", "Manly", "Mona Vale", "Narrabeen", "Newport", "Palm Beach", "Warriewood"]
}, {
  name: "Western Sydney",
  suburbs: ["Auburn", "Bankstown", "Bass Hill", "Blacktown", "Cabramatta", "Canterbury", "Chester Hill", "Fairfield", "Granville", "Greystanes", "Guildford", "Liverpool", "Merrylands", "Mount Druitt", "Parramatta", "Penrith", "Prospect", "Quakers Hill", "Rooty Hill", "Seven Hills", "Smithfield", "St Marys", "Toongabbie", "Wentworthville", "Wetherill Park", "Yagoona"]
}, {
  name: "Hills District",
  suburbs: ["Baulkham Hills", "Beaumont Hills", "Bella Vista", "Carlingford", "Castle Hill", "Cherrybrook", "Dural", "Eastwood", "Epping", "Glenhaven", "Kellyville", "Kellyville Ridge", "Marsden Park", "North Rocks", "Norwest", "Rouse Hill", "Schofields", "The Ponds", "West Pennant Hills"]
}, {
  name: "Sutherland Shire",
  suburbs: ["Caringbah", "Como", "Cronulla", "Engadine", "Gymea", "Heathcote", "Kirrawee", "Menai", "Miranda", "Oyster Bay", "Sutherland", "Sylvania"]
}, {
  name: "Sydney CBD & South",
  suburbs: ["Alexandria", "Barangaroo", "Beaconsfield", "Chippendale", "Darlinghurst", "Erskineville", "Haymarket", "Pyrmont", "Redfern", "Sydney CBD", "The Rocks", "Ultimo", "Waterloo", "Woolloomooloo"]
}, {
  name: "Wollongong",
  suburbs: ["Albion Park", "Austinmer", "Bellambi", "Berkeley", "Bulli", "Coledale", "Coniston", "Corrimal", "Dapto", "Fairy Meadow", "Figtree", "Gerringong", "Helensburgh", "Horsley", "Kiama", "Lake Heights", "Mount Kembla", "Mount Pleasant", "North Wollongong", "Oak Flats", "Port Kembla", "Shellharbour", "Shell Cove", "Thirroul", "Towradgi", "Unanderra", "Warrawong", "Warilla", "Windang", "Wollongong", "Woonona"]
}, {
  name: "Central Coast",
  suburbs: ["Avoca Beach", "Bateau Bay", "Berkeley Vale", "Budgewoi", "Copacabana", "East Gosford", "Empire Bay", "Ettalong Beach", "Erina", "Forresters Beach", "Gosford", "Kanwal", "Killarney Vale", "Kincumber", "Lake Haven", "Lake Munmorah", "Long Jetty", "MacMasters Beach", "Norah Head", "Pearl Beach", "Saratoga", "Shelly Beach", "Terrigal", "The Entrance", "Toukley", "Tuggerah", "Tumbi Umbi", "Umina Beach", "Wamberal", "West Gosford", "Woy Woy", "Wyong"]
}];
const totalSuburbs = regions.reduce((sum, r) => sum + r.suburbs.length, 0);
export default function ServicesHubServiceAreas() {
  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState("");
  const [activeRegion, setActiveRegion] = useState<string>("All Areas");
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisible(true);
    }, {
      threshold: 0.12
    });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return regions.filter(r => activeRegion === "All Areas" || r.name === activeRegion).map(r => ({
      ...r,
      suburbs: q ? r.suburbs.filter(s => s.toLowerCase().includes(q)) : r.suburbs
    })).filter(r => r.suburbs.length > 0);
  }, [query, activeRegion]);
  const chips = ["All Areas", ...regions.map(r => r.name)];
  return <section id="services-hub-service-areas" ref={sectionRef} style={HEAD} className="relative bg-[linear-gradient(135deg,#081438_0%,#0d1e3a_60%,#00285a_100%)] pt-16 pb-20 md:pt-20 md:pb-24 overflow-hidden text-white">
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-[2px] bg-[linear-gradient(90deg,transparent_5%,#F5A623_30%,#ffc142_50%,#F5A623_70%,transparent_95%)] z-10" />
      <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:24px_24px] pointer-events-none z-0" />
      <div aria-hidden="true" className="absolute -top-[80px] -right-[80px] w-[400px] h-[400px] bg-[radial-gradient(circle,rgba(245,166,35,0.05)_0%,transparent_70%)] pointer-events-none z-0 hidden md:block" />

      <div className="relative z-10 max-w-[1240px] mx-auto px-5 md:px-8">

        {/* HEADER + STATS */}
        <div className={`grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-14 mb-10 md:mb-12 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div>
            <div className="w-10 h-[2px] bg-[#F5A623] rounded-md mb-3" aria-hidden="true" />
            <span style={LABEL} className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[#F5A623] mb-4">
              Where We Work
            </span>
            <h2 style={HEAD} className="text-[clamp(28px,3.4vw,42px)] font-extrabold text-white leading-[1.08] tracking-[-0.02em] mb-4">
              Servicing <span className="text-[#F5A623]">every garage door</span><br className="hidden sm:block" /> across Greater Sydney.
            </h2>
            <p style={HEAD} className="text-[16px] text-white/55 leading-[1.7] max-w-[560px]">
              From single-door inner-city homes to multi-bay properties on the Central Coast — same team, same standard, across <strong className="text-white font-bold">{totalSuburbs}+ suburbs</strong> in Sydney, Wollongong and the Central Coast.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 self-end">
            <div className="text-center px-2">
              <Layers className="w-5 h-5 mx-auto text-[#F5A623] mb-2" strokeWidth={2.3} aria-hidden="true" />
              <p style={HEAD} className="text-white text-[22px] md:text-[26px] font-extrabold leading-none whitespace-nowrap">{totalSuburbs}+</p>
              <p style={LABEL} className="text-[10px] uppercase tracking-[0.1em] text-white/45 font-semibold mt-1.5 leading-tight">Suburbs<br />Covered</p>
            </div>
            <div className="text-center px-2 border-x border-white/8">
              <Star className="w-5 h-5 mx-auto text-[#F5A623] mb-2" strokeWidth={2.3} aria-hidden="true" fill="#F5A623" />
              <p style={HEAD} className="text-white text-[22px] md:text-[26px] font-extrabold leading-none whitespace-nowrap">5.0</p>
              <p style={LABEL} className="text-[10px] uppercase tracking-[0.1em] text-white/45 font-semibold mt-1.5 leading-tight">Google<br />Rating</p>
            </div>
            <div className="text-center px-2">
              <BadgeCheck className="w-5 h-5 mx-auto text-[#F5A623] mb-2" strokeWidth={2.3} aria-hidden="true" />
              <p style={HEAD} className="text-white text-[22px] md:text-[26px] font-extrabold leading-none whitespace-nowrap">7 Days</p>
              <p style={LABEL} className="text-[10px] uppercase tracking-[0.1em] text-white/45 font-semibold mt-1.5 leading-tight">A Week<br />Available</p>
            </div>
          </div>
        </div>

        {/* SEARCH */}
        <div className={`relative max-w-[640px] mb-5 transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <label htmlFor="suburb-search" className="sr-only">Search for your suburb</label>
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-white/40 pointer-events-none" strokeWidth={2.2} aria-hidden="true" />
          <input id="suburb-search" type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Find your suburb — e.g. Bondi, Wollongong, Terrigal..." style={HEAD} className="w-full bg-white/[0.05] border border-white/12 rounded-xl pl-12 pr-12 py-3.5 text-white text-[15px] placeholder:text-white/35 outline-none transition-all focus:border-[#F5A623] focus:bg-white/[0.08] focus:ring-4 focus:ring-[#F5A623]/15" />
          {query && <button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-white/8 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F5A623]">
              <X className="w-4 h-4 text-white/55" strokeWidth={2.4} aria-hidden="true" />
            </button>}
        </div>

        {/* CHIPS */}
        <div className={`flex flex-wrap gap-2 mb-10 md:mb-12 transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
        transitionDelay: visible ? "150ms" : "0ms"
      }} role="tablist" aria-label="Filter by region">
          {chips.map(chip => {
          const isActive = chip === activeRegion;
          return <button key={chip} type="button" role="tab" aria-selected={isActive} onClick={() => setActiveRegion(chip)} style={LABEL} className={`text-[13px] font-semibold px-4 py-2 rounded-lg transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F5A623] focus-visible:outline-offset-2 ${isActive ? "bg-[#F5A623] text-[#081438] shadow-md shadow-[#F5A623]/25" : "bg-white/[0.04] text-white/70 border border-white/12 hover:bg-white/[0.08] hover:text-white"}`}>
                {chip}
              </button>;
        })}
        </div>

        {/* REGIONS GRID */}
        {filtered.length === 0 ? <div className="text-center py-16 bg-white/[0.03] border border-white/8 rounded-xl">
            <MapPin className="w-8 h-8 mx-auto text-[#F5A623]/60 mb-3" strokeWidth={2} aria-hidden="true" />
            <p style={HEAD} className="text-white text-[16px] font-semibold mb-1.5">
              No suburbs match "{query}"
            </p>
            <p style={HEAD} className="text-white/55 text-[14px]">
              We still might cover your area — call us to check.
            </p>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
            {filtered.map(region => <div key={region.name}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span aria-hidden="true" className="w-2 h-2 rounded-full bg-[#F5A623]" />
                    <h3 style={LABEL} className="text-[12px] font-bold uppercase tracking-[0.14em] text-white">
                      {region.name}
                    </h3>
                  </div>
                  <span style={LABEL} className="text-[11px] font-semibold text-white/35">
                    {region.suburbs.length} suburb{region.suburbs.length === 1 ? "" : "s"}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {region.suburbs.map(suburb => <span key={`${region.name}-${suburb}`} style={LABEL} className="inline-flex items-center gap-1.5 text-[13px] text-white/70 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/8 hover:bg-white/[0.06] hover:border-[#F5A623]/25 transition-all">
                      <MapPin className="w-3 h-3 text-white/35" strokeWidth={2.2} aria-hidden="true" />
                      {suburb}
                    </span>)}
                </div>
              </div>)}
          </div>}

        {/* BOTTOM CTA BANNER */}
        <div className={`relative mt-12 md:mt-14 rounded-xl p-7 md:p-9 overflow-hidden transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        background: "linear-gradient(135deg, rgba(245,166,35,0.06) 0%, rgba(8,20,56,0.4) 100%)",
        border: "1px solid rgba(245,166,35,0.18)",
        transitionDelay: visible ? "300ms" : "0ms"
      }}>
          <span aria-hidden="true" className="absolute -top-[60px] -right-[60px] w-[200px] h-[200px] bg-[radial-gradient(circle,rgba(245,166,35,0.1)_0%,transparent_70%)] pointer-events-none" />

          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <span style={LABEL} className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[#F5A623] mb-2">
                Outside These Areas?
              </span>
              <h3 style={HEAD} className="text-white text-[22px] md:text-[26px] font-extrabold leading-tight mb-1.5">
                Don't see your suburb? <span className="text-[#F5A623]">We probably still come to you.</span>
              </h3>
              <p style={HEAD} className="text-white/55 text-[14.5px] leading-relaxed">
                Our mobile team extends to most of Greater Sydney on request. Call to confirm coverage for your address.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Link to="tel:0485000933" aria-label="Call Gold Garage Door" style={{
              ...LABEL,
              background: "linear-gradient(135deg, #F5A623 0%, #d4a017 100%)",
              boxShadow: "0 10px 25px rgba(245,166,35,0.3)"
            }} className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-[14px] font-bold text-white no-underline transition-all hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F5A623] focus-visible:outline-offset-4">
                Call — 0485 000 933
              </Link>
              <Link to="https://wa.me/61485000933?text=Hi%20Gold%20Garage%20Door%2C%20do%20you%20cover%20my%20suburb%3F" aria-label="Ask via WhatsApp if Gold Garage Door covers your suburb" style={LABEL} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-[14px] font-bold text-white bg-transparent border border-white/25 hover:bg-white/8 hover:border-white/45 transition-all no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F5A623] focus-visible:outline-offset-4">
                WhatsApp Us →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}